import React, { useEffect, useState } from 'react';
import {
  Users, UserCheck, UserX, RefreshCw, ClipboardCheck, Brain, Loader2,
  Cake, Briefcase, DollarSign, AlertCircle,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, Legend,
  ComposedChart, Line, AreaChart, Area, PieChart, Pie, CartesianGrid,
} from 'recharts';
import {
  atlasApi, AtlasUser, DashboardResumo, TurnoverMes, HeadcountMes, DiscFator,
} from '../shared/atlasApi';

interface DashboardProps {
  user: AtlasUser;
}

const fmtInt = new Intl.NumberFormat('pt-BR');
const fmtBRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const nfInt = (n: number | null | undefined) => fmtInt.format(Math.round(n || 0));
const nfBRL = (n: number | null | undefined) => fmtBRL.format(n || 0);

const DISC_CORES: Record<DiscFator, string> = { D: '#e11d48', I: '#f59e0b', S: '#10b981', C: '#3b82f6' };
const DISC_LABEL: Record<DiscFator, string> = { D: 'Dominância', I: 'Influência', S: 'Estabilidade', C: 'Conformidade' };
const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

const MESES_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: string | number; color: string }>
  = ({ icon, label, value, color }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>{icon}</div>
    <div className="min-w-0">
      <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
    </div>
  </div>
);

const CardTitulo: React.FC<{ children: React.ReactNode; extra?: React.ReactNode }> = ({ children, extra }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{children}</h3>
    {extra}
  </div>
);

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [resumo, setResumo] = useState<DashboardResumo | null>(null);
  const [turnover, setTurnover] = useState<TurnoverMes[]>([]);
  const [headcountHist, setHeadcountHist] = useState<HeadcountMes[]>([]);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Filtros
  const [cc, setCc] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [filtrando, setFiltrando] = useState(false);
  // Opções estáveis dos dropdowns (capturadas na carga inicial, sem filtro)
  const [ccOptsBase, setCcOptsBase] = useState<DashboardResumo['headcount']['por_centro_custo']>([]);
  const [deptoOptsBase, setDeptoOptsBase] = useState<DashboardResumo['headcount']['por_departamento']>([]);

  // Carga inicial (tudo em paralelo)
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    Promise.all([
      atlasApi.getDashboardResumo(user),
      atlasApi.getTurnover(user, 12),
      atlasApi.getHeadcountHistorico(user, 12),
      atlasApi.syncStatus(user).catch(() => null),
    ])
      .then(([res, turn, hist, status]) => {
        if (cancel) return;
        setResumo(res);
        setCcOptsBase(res?.headcount?.por_centro_custo || []);
        setDeptoOptsBase(res?.headcount?.por_departamento || []);
        setTurnover(turn?.meses || []);
        setHeadcountHist(hist?.meses || []);
        setSyncStatus(status);
      })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar o dashboard'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user]);

  // Refaz resumo + turnover + headcount histórico quando os filtros mudam
  useEffect(() => {
    if (loading) return; // ignora durante carga inicial
    let cancel = false;
    setFiltrando(true);
    const filtros = {
      cc: cc || undefined,
      departamento: departamento || undefined,
    };
    Promise.all([
      atlasApi.getDashboardResumo(user, filtros),
      atlasApi.getTurnover(user, 12, filtros),
      atlasApi.getHeadcountHistorico(user, 12, filtros),
    ])
      .then(([res, turn, hist]) => {
        if (cancel) return;
        setResumo(res);
        setTurnover(turn?.meses || []);
        setHeadcountHist(hist?.meses || []);
      })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao aplicar filtros'); })
      .finally(() => { if (!cancel) setFiltrando(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cc, departamento]);

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin mx-auto mb-3" />
        Carregando indicadores de pessoas...
      </div>
    );
  }

  // Opções de filtro estáveis (capturadas na carga inicial, sem filtro aplicado)
  const ccOpts = ccOptsBase;
  const deptoOpts = deptoOptsBase;

  // Datasets derivados
  const turnoverData = turnover.map(m => ({
    label: m.label,
    Admitidos: m.admitidos,
    Demitidos: m.demitidos,
    'Turnover %': m.turnover_pct,
  }));

  const headcountData = headcountHist.map(m => ({ label: m.label, Headcount: m.headcount }));

  const ccBarData = [...ccOpts]
    .sort((a, b) => b.total - a.total)
    .map(c => ({ nome: c.nome || c.codigo || '—', codigo: String(c.codigo ?? ''), total: c.total }));

  const discDist = resumo?.disc?.distribuicao || { D: 0, I: 0, S: 0, C: 0 };
  const discData = FATORES.map(f => ({ fator: f, label: DISC_LABEL[f], total: discDist[f] || 0 }));
  const discTemDados = discData.some(d => d.total > 0);

  const idadeData = resumo?.idade?.distribuicao || [];
  const tempoCasaData = resumo?.tempo_casa?.distribuicao || [];

  const aniversariantes = resumo?.aniversariantes_30d || [];
  const vagas = resumo?.vagas;
  const custo = resumo?.custo_quadro || null;
  const custoBarData = (custo?.por_centro_custo || [])
    .slice()
    .sort((a, b) => b.total - a.total)
    .map(c => ({ nome: c.nome || c.codigo || '—', codigo: String(c.codigo ?? ''), total: c.total }));

  const ordinalDia = (a: { dia: number; mes: number }) =>
    `${String(a.dia).padStart(2, '0')}/${MESES_PT[(a.mes - 1 + 12) % 12]}`;

  // Drill-down BI: clicar numa barra de CC afunila o dashboard por aquele centro de custo
  const toggleCcFiltro = (codigo: string) => {
    if (!codigo) return;
    setCc(prev => (prev === codigo ? '' : codigo));
  };

  // Rótulos legíveis dos filtros ativos (para o breadcrumb de afunilamento)
  const ccAtivoLabel = cc
    ? (ccOpts.find(c => String(c.codigo) === cc)?.nome || cc)
    : null;
  const deptoAtivoLabel = departamento
    ? (deptoOpts.find(d => String(d.codigo) === departamento)?.codigo || departamento)
    : null;

  return (
    <div className="p-6 space-y-6">
      {erro && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {erro}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={cc}
          onChange={e => setCc(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          <option value="">Todos os centros de custo</option>
          {ccOpts.map(c => (
            <option key={String(c.codigo)} value={String(c.codigo)}>
              {c.nome || c.codigo} ({c.total})
            </option>
          ))}
        </select>
        <select
          value={departamento}
          onChange={e => setDepartamento(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          <option value="">Todos os departamentos</option>
          {deptoOpts.map(d => (
            <option key={String(d.codigo)} value={String(d.codigo)}>
              {d.codigo} ({d.total})
            </option>
          ))}
        </select>
        {(cc || departamento) && (
          <button
            onClick={() => { setCc(''); setDepartamento(''); }}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
          >
            Limpar filtros
          </button>
        )}
        {filtrando && (
          <span className="text-xs text-gray-400 inline-flex items-center gap-1">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> atualizando...
          </span>
        )}
      </div>

      {/* Breadcrumb de afunilamento (drill-down BI) */}
      {(ccAtivoLabel || deptoAtivoLabel) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Afunilando por:</span>
          {ccAtivoLabel && (
            <button
              onClick={() => setCc('')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200 transition"
              title="Remover filtro de centro de custo"
            >
              Centro de custo: {ccAtivoLabel}
              <span className="text-purple-400">✕</span>
            </button>
          )}
          {deptoAtivoLabel && (
            <button
              onClick={() => setDepartamento('')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200 transition"
              title="Remover filtro de departamento"
            >
              Departamento: {deptoAtivoLabel}
              <span className="text-purple-400">✕</span>
            </button>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Stat icon={<Users className="w-6 h-6 text-purple-600" />}
              label="Headcount" value={nfInt(resumo?.headcount?.total)} color="bg-purple-100" />
        <Stat icon={<UserCheck className="w-6 h-6 text-emerald-600" />}
              label="Ativos" value={nfInt(resumo?.headcount?.ativos)} color="bg-emerald-100" />
        <Stat icon={<UserX className="w-6 h-6 text-gray-500" />}
              label="Demitidos" value={nfInt(resumo?.headcount?.demitidos)} color="bg-gray-100" />
        <Stat icon={<ClipboardCheck className="w-6 h-6 text-sky-600" />}
              label="Fichas completas" value={`${Math.round(resumo?.fichas_completas_pct || 0)}%`} color="bg-sky-100" />
        <Stat icon={<Brain className="w-6 h-6 text-amber-600" />}
              label="DISC respondido" value={`${Math.round(resumo?.disc?.respondido_pct || 0)}%`} color="bg-amber-100" />
      </div>

      {/* Turnover + Headcount histórico */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo>Turnover (12 meses)</CardTitulo>
          {turnoverData.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">Sem dados de movimentação.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={turnoverData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="Admitidos" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="Demitidos" fill="#e11d48" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="Turnover %" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo>Headcount histórico</CardTitulo>
          {headcountData.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">Sem histórico de headcount.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={headcountData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradHeadcount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="Headcount" stroke="#7c3aed" strokeWidth={2} fill="url(#gradHeadcount)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Headcount por CC + DISC */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo extra={<span className="text-[11px] text-gray-400 normal-case font-normal">clique para afunilar</span>}>Headcount por centro de custo</CardTitulo>
          {ccBarData.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">Sem dados por centro de custo.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, ccBarData.length * 30)}>
              <BarChart data={ccBarData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="nome" width={140} tick={{ fontSize: 11 }} interval={0} />
                <Tooltip cursor={{ fill: 'rgba(124,58,237,0.06)' }} />
                <Bar
                  dataKey="total"
                  radius={[0, 4, 4, 0]}
                  cursor="pointer"
                  onClick={(d: any) => toggleCcFiltro(d?.codigo)}
                >
                  {ccBarData.map(c => (
                    <Cell
                      key={c.codigo || c.nome}
                      fill={cc && cc === c.codigo ? '#5b21b6' : '#7c3aed'}
                      fillOpacity={cc && cc !== c.codigo ? 0.35 : 1}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo extra={<span className="text-xs text-gray-400">{Math.round(resumo?.disc?.respondido_pct || 0)}% respondido</span>}>
            Distribuição DISC
          </CardTitulo>
          {!discTemDados ? (
            <p className="text-sm text-gray-400 p-6 text-center">Ninguém respondeu o DISC ainda.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={discData}
                  dataKey="total"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(e: any) => `${e.fator}: ${e.total}`}
                >
                  {discData.map(d => <Cell key={d.fator} fill={DISC_CORES[d.fator as DiscFator]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Faixa etária + Tempo de casa */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo extra={<span className="text-xs text-gray-400">média {(resumo?.idade?.media || 0).toFixed(1)} anos</span>}>
            Faixa etária
          </CardTitulo>
          {idadeData.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">Sem dados de idade.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={idadeData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="faixa" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo extra={
            <span className="text-xs text-gray-400">
              média {(resumo?.tempo_casa?.media_anos || 0).toFixed(1)}a · mediana {(resumo?.tempo_casa?.mediana_anos || 0).toFixed(1)}a
            </span>
          }>
            Tempo de casa
          </CardTitulo>
          {tempoCasaData.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">Sem dados de tempo de casa.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={tempoCasaData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="faixa" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Aniversariantes + Vagas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-5 lg:col-span-2">
          <CardTitulo extra={<Cake className="w-4 h-4 text-rose-400" />}>Aniversariantes (30 dias)</CardTitulo>
          {aniversariantes.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">Nenhum aniversariante nos próximos 30 dias.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {aniversariantes.map(a => (
                <li key={a.funccodi} className="flex items-center gap-3 py-2.5">
                  <div className="w-9 h-9 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center flex-shrink-0">
                    <Cake className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{a.nome || `#${a.funccodi}`}</div>
                    <div className="text-xs text-gray-400">{ordinalDia(a)}</div>
                  </div>
                  <span className="text-xs font-medium text-gray-500 flex-shrink-0">
                    {a.dias_ate === 0 ? 'hoje' : a.dias_ate === 1 ? 'amanhã' : `em ${a.dias_ate} dias`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo extra={<Briefcase className="w-4 h-4 text-purple-400" />}>Vagas</CardTitulo>
          <div className="text-3xl font-bold text-gray-900">{nfInt(vagas?.abertas)}</div>
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">abertas</div>
          {vagas && Object.keys(vagas.por_status || {}).length > 0 ? (
            <ul className="space-y-1.5">
              {Object.entries(vagas.por_status).map(([status, total]) => (
                <li key={status} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 capitalize">{status}</span>
                  <span className="font-semibold text-gray-900">{nfInt(total)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-400">Sem vagas registradas.</p>
          )}
        </div>
      </div>

      {/* Custo de quadro — só rh_total (custo_quadro != null) */}
      {custo && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <CardTitulo extra={custo.total > 0 ? <span className="inline-flex items-center gap-2 text-[11px] text-gray-400 normal-case font-normal">clique para afunilar <DollarSign className="w-4 h-4 text-emerald-500" /></span> : undefined}>Custo de quadro</CardTitulo>
          {custo.total > 0 ? (
            <>
              <div className="text-3xl font-bold text-emerald-600">{nfBRL(custo.total)}</div>
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-4">folha total / mês</div>
            </>
          ) : (
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-100 p-4 text-amber-800">
              <DollarSign className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-500" />
              <div className="text-sm">
                <p className="font-semibold">Sem dados de salário disponíveis</p>
                <p className="text-amber-700/80 mt-0.5">
                  Os valores de salário ainda não foram sincronizados do GIX. Execute uma sincronização
                  para que o custo de quadro seja calculado.
                </p>
              </div>
            </div>
          )}
          {custoBarData.length > 0 && (
            <ResponsiveContainer width="100%" height={Math.max(200, custoBarData.length * 30)}>
              <BarChart data={custoBarData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => nfBRL(v)} />
                <YAxis type="category" dataKey="nome" width={140} tick={{ fontSize: 11 }} interval={0} />
                <Tooltip formatter={(v: any) => nfBRL(Number(v))} cursor={{ fill: 'rgba(16,185,129,0.06)' }} />
                <Bar
                  dataKey="total"
                  radius={[0, 4, 4, 0]}
                  cursor="pointer"
                  onClick={(d: any) => toggleCcFiltro(d?.codigo)}
                >
                  {custoBarData.map(c => (
                    <Cell
                      key={c.codigo || c.nome}
                      fill={cc && cc === c.codigo ? '#047857' : '#10b981'}
                      fillOpacity={cc && cc !== c.codigo ? 0.35 : 1}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Sincronização com GIX (mantido no rodapé) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900">Sincronização com GIX</h3>
            <p className="text-xs text-gray-500">Dados do ERP de RH</p>
          </div>
          <RefreshCw className="w-5 h-5 text-gray-400" />
        </div>
        {syncStatus ? (
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <dt className="text-[11px] text-gray-400 uppercase tracking-wider">Última execução</dt>
              <dd className="font-medium text-gray-700">
                {syncStatus.ultima_execucao ? new Date(syncStatus.ultima_execucao).toLocaleString('pt-BR') : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] text-gray-400 uppercase tracking-wider">Resultado</dt>
              <dd className={`font-medium ${syncStatus.ultimo_resultado === 'erro' ? 'text-rose-600' : 'text-emerald-600'}`}>
                {syncStatus.ultimo_resultado || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] text-gray-400 uppercase tracking-wider">Atualizados</dt>
              <dd className="font-medium text-gray-700">{syncStatus.total_atualizados ?? 0}</dd>
            </div>
            <div>
              <dt className="text-[11px] text-gray-400 uppercase tracking-wider">Inseridos</dt>
              <dd className="font-medium text-gray-700">{syncStatus.total_inseridos ?? 0}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-gray-400 italic">Sem dados de sincronização ainda.</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
